import { api_fetch } from "@/api/client.tsx"
import { ClickToCopyRemotePromoId } from "@/components/click_to_copy_remote_promo_id.tsx"
import { Button } from "@/components/ui/button.tsx"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx"
import { ActiveFilterIcon } from "@/global_filter"
import { classes } from "@/utils"
import { Check, ChevronDown, ChevronUp, ChevronsUpDown, Star } from "lucide-react"
import { useEffect, useRef, useState, useMemo, useCallback } from "preact/hooks"
import { format, parseISO } from "date-fns"

export interface PromotionMatch {
  promotion: {
    id: string
    remote_promo_id?: string
    name: string
    description?: string
    start_date?: string
    end_date?: string
    discount_amount?: number
    discount_percentage?: number
    customer?: string
    retailer?: string
    products: string[]
    promotion_type?: string
  }
  match_score: number
  match_summary: string
  customer_match: boolean
  retailer_match: boolean
  product_match: boolean
  date_match: boolean
  rate_match: boolean
}

export interface PromotionSearchContextSeed {
  customer_name?: string
  retailer_name?: string | null
  source?: string
  invoice_date?: string
  execution_date?: string
}

interface PromotionSelectorProps {
  deductionId: string
  onSelect: (promotion: PromotionMatch) => void
  selectedPromotionId?: string
  className?: string
  /** When set (e.g. from accounting transaction), structured filters initialize from deduction context */
  searchContextSeed?: PromotionSearchContextSeed
}

/** Row fields a promotion search seed can be derived from (deduction list row or transaction). */
export interface PromotionSeedSource {
  customer_name?: string
  retailer_name?: string | null
  source?: string
  invoice_date?: string
  execution_date?: string
}

/** The value every row shares for a field, or undefined when they disagree or all are empty. */
function shared_seed_value(
  rows: PromotionSeedSource[],
  read: (row: PromotionSeedSource) => string | null | undefined
): string | undefined {
  const values = new Set(rows.map((row: PromotionSeedSource) => (read(row) ?? "").trim()))
  if (values.size !== 1) return undefined
  const [only] = [...values]
  return only === "" ? undefined : only
}

/**
 * Seed for a multi-row selection (bulk match). Keeps only the fields every selected row agrees on,
 * so a selection spanning several dates or customers does not filter the promotion list down to
 * whatever the first row happened to be.
 */
export function buildPromotionSearchSeedFromSelection(
  rows: PromotionSeedSource[]
): PromotionSearchContextSeed | undefined {
  if (rows.length === 0) return undefined
  return {
    customer_name: shared_seed_value(rows, (row: PromotionSeedSource) => row.customer_name),
    retailer_name: shared_seed_value(rows, (row: PromotionSeedSource) => row.retailer_name),
    source: shared_seed_value(rows, (row: PromotionSeedSource) => row.source),
    invoice_date: shared_seed_value(rows, (row: PromotionSeedSource) => row.invoice_date),
    execution_date: shared_seed_value(rows, (row: PromotionSeedSource) => row.execution_date),
  }
}

export function buildPromotionSearchSeedFromTransaction(tx: PromotionSeedSource): PromotionSearchContextSeed {
  return {
    customer_name: tx.customer_name,
    retailer_name: tx.retailer_name ?? undefined,
    source: tx.source,
    invoice_date: tx.invoice_date,
    execution_date: tx.execution_date,
  }
}

/**
 * Default as-of date for the structured filters. Prefers the execution date (the day the promotion
 * actually ran) over the invoice date, since matching on invoice date finds the wrong promotions.
 * Invalid values like "Unknown" fall through to the next candidate.
 */
export function resolve_seed_as_of_date(
  seed: Pick<PromotionSearchContextSeed, "execution_date" | "invoice_date">
): string {
  for (const raw of [seed.execution_date, seed.invoice_date]) {
    const trimmed = raw?.trim()
    if (!trimmed || trimmed.toLowerCase() === "unknown") continue
    try {
      const d = parseISO(trimmed.length <= 10 ? `${trimmed}T12:00:00` : trimmed)
      if (!Number.isNaN(d.getTime())) return format(d, "yyyy-MM-dd")
    } catch {
      // fall through to the next candidate
    }
  }
  return ""
}

/**
 * When the seeded customer/date filters hide every match, progressively drop them (date first,
 * then customer, then both) until at least one match passes, instead of making the user clear
 * the filters by hand.
 */
export function relax_seeded_filters(
  matches: PromotionMatch[],
  seed: { customerNeedle: string; asOfDateStr: string }
): { customerNeedle: string; asOfDateStr: string } {
  const candidates = [
    seed,
    { customerNeedle: seed.customerNeedle, asOfDateStr: "" },
    { customerNeedle: "", asOfDateStr: seed.asOfDateStr },
    { customerNeedle: "", asOfDateStr: "" },
  ]
  for (const candidate of candidates) {
    const has_match = matches.some(match =>
      matchPassesStructuredFilters(match, { ...candidate, promoTypeNeedle: "" })
    )
    if (has_match) return candidate
  }
  return { customerNeedle: "", asOfDateStr: "" }
}

/** Inclusive range using calendar YYYY-MM-DD strings; missing start or end is unbounded on that side. */
export function promotionAsOfDateInRange(
  start_date: string | undefined,
  end_date: string | undefined,
  as_of_calendar_day: string
): boolean {
  const day = as_of_calendar_day.trim().slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false
  const start = start_date?.trim().slice(0, 10)
  const end = end_date?.trim().slice(0, 10)
  if (!start && !end) return true
  if (start && day < start) return false
  if (end && day > end) return false
  return true
}

export function matchPassesStructuredFilters(
  match: PromotionMatch,
  filters: { customerNeedle: string; promoTypeNeedle: string; asOfDateStr: string }
): boolean {
  const cust = filters.customerNeedle.trim().toLowerCase()
  if (cust) {
    const promoCust = (match.promotion.customer || "").toLowerCase()
    const promoRet = (match.promotion.retailer || "").toLowerCase()
    if (!promoCust.includes(cust) && !promoRet.includes(cust)) {
      return false
    }
  }
  const pt = filters.promoTypeNeedle.trim().toLowerCase()
  if (pt) {
    const ty = (match.promotion.promotion_type || "").toLowerCase()
    if (!ty.includes(pt)) return false
  }
  const d = filters.asOfDateStr.trim()
  if (d) {
    if (!promotionAsOfDateInRange(match.promotion.start_date, match.promotion.end_date, d)) {
      return false
    }
  }
  return true
}

function getPromotionSearchValues(
  match: PromotionMatch,
  formatDate: (dateString: string) => { display: string; numeric: string; full: string }
): string[] {
  const values: string[] = [
    match.promotion.name,
    match.promotion.description || "",
    match.promotion.customer || "",
    match.promotion.retailer || "",
    match.promotion.remote_promo_id || "",
    ...match.promotion.products,
  ]

  if (match.promotion.start_date && match.promotion.end_date) {
    const startFormatted = formatDate(match.promotion.start_date)
    const endFormatted = formatDate(match.promotion.end_date)
    values.push(
      startFormatted.display,
      endFormatted.display,
      startFormatted.numeric,
      endFormatted.numeric,
      startFormatted.full,
      endFormatted.full
    )
  }

  return values.map(value => value.toLowerCase())
}

function getSearchTokens(searchValue: string): string[] {
  return searchValue
    .toLowerCase()
    .split(/\s+/)
    .map(token => token.trim())
    .filter(Boolean)
}

function matchesExactPhrase(
  match: PromotionMatch,
  searchLower: string,
  formatDate: (dateString: string) => { display: string; numeric: string; full: string }
): boolean {
  const searchValues = getPromotionSearchValues(match, formatDate)
  return searchValues.some(value => value.includes(searchLower))
}

function matchesTokenFallback(
  match: PromotionMatch,
  searchTokens: string[],
  formatDate: (dateString: string) => { display: string; numeric: string; full: string }
): boolean {
  if (searchTokens.length === 0) return true
  const searchValues = getPromotionSearchValues(match, formatDate)
  return searchTokens.every(token => searchValues.some(value => value.includes(token)))
}

export function PromotionSelector({
  deductionId,
  onSelect,
  selectedPromotionId,
  className,
  searchContextSeed,
}: PromotionSelectorProps) {
  const [open, setOpen] = useState(false)
  const [matches, setMatches] = useState<PromotionMatch[]>([])
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [filterCustomer, setFilterCustomer] = useState("")
  const [filterPromoType, setFilterPromoType] = useState("")
  const [filterAsOfDate, setFilterAsOfDate] = useState("")
  const [expandedPromotionIds, setExpandedPromotionIds] = useState<string[]>([])
  /** Last filters applied from the seed; auto-relaxation only runs while the user hasn't edited them. */
  const [seedFilters, setSeedFilters] = useState<{ customer: string; as_of: string } | null>(null)
  const commandListRef = useRef<HTMLDivElement>(null)

  // Load promotion matches when component mounts or deductionId changes
  useEffect(() => {
    if (deductionId) {
      loadPromotionMatches()
    }
  }, [deductionId])

  useEffect(() => {
    if (!searchContextSeed) return
    const cust =
      searchContextSeed.customer_name?.trim() ||
      searchContextSeed.retailer_name?.trim() ||
      searchContextSeed.source?.trim() ||
      ""
    const as_of = resolve_seed_as_of_date(searchContextSeed)
    setFilterCustomer(cust)
    setFilterPromoType("")
    setFilterAsOfDate(as_of)
    setSeedFilters({ customer: cust, as_of })
  }, [
    deductionId,
    searchContextSeed?.customer_name,
    searchContextSeed?.retailer_name,
    searchContextSeed?.source,
    searchContextSeed?.invoice_date,
    searchContextSeed?.execution_date,
  ])

  const loadPromotionMatches = useCallback(async () => {
    setLoading(true)
    try {
      const result = await api_fetch<PromotionMatch[]>("/promotion-matches", {
        method: "POST",
        body: {
          deduction_id: deductionId
        }, 
        use_cache: true
      })
      
      if (result.ok) {
        setMatches(result.value.data)
      } else {
        console.error("Failed to load promotion matches:", result.error)
        setMatches([])
      }
    } catch (error) {
      console.error("Error loading promotion matches:", error)
      setMatches([])
    } finally {
      setLoading(false)
    }
  }, [deductionId])

  // If the seeded filters hide every loaded match, relax them automatically so the user
  // isn't presented with an empty list they have to clear filters to escape.
  useEffect(() => {
    if (!seedFilters || loading || matches.length === 0) return
    // The user edited the filters since seeding — leave their input alone
    if (
      filterCustomer !== seedFilters.customer ||
      filterAsOfDate !== seedFilters.as_of ||
      filterPromoType !== ""
    ) {
      return
    }
    const relaxed = relax_seeded_filters(matches, {
      customerNeedle: seedFilters.customer,
      asOfDateStr: seedFilters.as_of,
    })
    if (relaxed.customerNeedle !== filterCustomer || relaxed.asOfDateStr !== filterAsOfDate) {
      setFilterCustomer(relaxed.customerNeedle)
      setFilterAsOfDate(relaxed.asOfDateStr)
      setSeedFilters({ customer: relaxed.customerNeedle, as_of: relaxed.asOfDateStr })
    }
  }, [matches, loading, seedFilters, filterCustomer, filterPromoType, filterAsOfDate])

  const handleSearchChange = useCallback(() => {
    // Reset scroll position when search input changes
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0
    }
  }, [])

  const handleSelect = useCallback((match: PromotionMatch) => {
    onSelect(match)
    setOpen(false)
  }, [onSelect])

  const toggleExpandedPromotion = useCallback((promotionId: string) => {
    setExpandedPromotionIds((previousIds: string[]) =>
      previousIds.includes(promotionId)
        ? previousIds.filter((id: string) => id !== promotionId)
        : [...previousIds, promotionId]
    )
  }, [])


  const selectedMatch = useMemo(() => 
    matches.find(m => m.promotion.id === selectedPromotionId), 
    [matches, selectedPromotionId]
  )

  // Helper function to format dates consistently
  const formatDate = useCallback((dateString: string) => {
    try {
      // Always use parseISO from date-fns as per frontend conventions
      const date = parseISO(dateString)
      
      // Return both formats for searching
      return {
        display: format(date, 'MMM d'),
        numeric: format(date, 'MM/dd/yy'),
        full: format(date, 'MMM d, yyyy')
      }
    } catch (error) {
      console.warn('Error formatting date:', dateString, error)
      return {
        display: dateString,
        numeric: dateString,
        full: dateString
      }
    }
  }, [])

  // Memoize the display formatting to prevent recalculation on every render
  const formatPromotionDisplay = useCallback((match: PromotionMatch) => {
    const { promotion } = match
    const score = Math.round(match.match_score * 100)
    
    let dateRange = ''
    if (promotion.start_date && promotion.end_date) {
      const startFormatted = formatDate(promotion.start_date)
      const endFormatted = formatDate(promotion.end_date)
      dateRange = `Dates: ${startFormatted.full} - ${endFormatted.full}`
    }
    
    return {
      title: promotion.name,
      subtitle: [
        promotion.customer && `Customer: ${promotion.customer}`,
        promotion.retailer && `Retailer: ${promotion.retailer}`,
        dateRange,
        promotion.discount_percentage && `Discount: ${promotion.discount_percentage}%`,
        promotion.discount_amount && `Amount: $${promotion.discount_amount}`
      ].filter(Boolean).join(" • "),
      score
    }
  }, [formatDate])

  const getMatchIndicators = useCallback((match: PromotionMatch) => {
    const indicators = []
    if (match.customer_match) indicators.push("C")
    if (match.retailer_match) indicators.push("R") 
    if (match.product_match) indicators.push("P")
    if (match.date_match) indicators.push("D")
    if (match.rate_match) indicators.push("$")
    return indicators.join("")
  }, [])

  // Debounced search value to prevent excessive filtering
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("")
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 150) // 150ms debounce
    
    return () => clearTimeout(timer)
  }, [searchValue])

  const structuredFilteredMatches = useMemo(() => {
    return matches.filter((match) =>
      matchPassesStructuredFilters(match, {
        customerNeedle: filterCustomer,
        promoTypeNeedle: filterPromoType,
        asOfDateStr: filterAsOfDate,
      })
    )
  }, [matches, filterCustomer, filterPromoType, filterAsOfDate])

  // Use debounced search value for filtering (after structured filters)
  const finalFilteredMatches = useMemo(() => {
    if (!debouncedSearchValue) return structuredFilteredMatches

    const searchLower = debouncedSearchValue.toLowerCase()
    const phraseMatches = structuredFilteredMatches.filter((match) =>
      matchesExactPhrase(match, searchLower, formatDate)
    )
    if (phraseMatches.length > 0) return phraseMatches

    const searchTokens = getSearchTokens(debouncedSearchValue)
    return structuredFilteredMatches.filter((match) => matchesTokenFallback(match, searchTokens, formatDate))
  }, [structuredFilteredMatches, debouncedSearchValue, formatDate])

  const hasActiveStructuredFilters = Boolean(filterCustomer.trim() || filterPromoType.trim() || filterAsOfDate.trim())

  function clearStructuredFilters() {
    setFilterCustomer("")
    setFilterPromoType("")
    setFilterAsOfDate("")
  }

  /** Wheel events bubble to portaled Radix Dialog and scroll the deduction modal instead of this list. */
  function stop_wheel_bubbling(e: WheelEvent) {
    e.stopPropagation()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`w-full justify-start text-left font-normal text-xs h-8 ${className || ""}`}>
          <div className="flex items-center gap-2 truncate w-full">
            {selectedMatch ? (
              <div className="flex items-center gap-2 truncate w-full min-w-0">
                <Star className="h-3 w-3 text-yellow-500 shrink-0" />
                <div className="flex items-center gap-2 truncate min-w-0 flex-1">
                  <span className="truncate font-medium">{selectedMatch.promotion.name}</span>
                  {selectedMatch.promotion.remote_promo_id && (
                    <ClickToCopyRemotePromoId
                      copyValue={selectedMatch.promotion.remote_promo_id}
                      stopPropagation
                      className="shrink-0 text-xs text-gray-600 hover:underline"
                    >
                      ({selectedMatch.promotion.remote_promo_id})
                    </ClickToCopyRemotePromoId>
                  )}
                </div>
                <span className="text-xs text-gray-500 shrink-0">
                  {Math.round(selectedMatch.match_score * 100)}%
                </span>
              </div>
            ) : (
              <>
                <ChevronsUpDown className="h-4 w-4 shrink-0" />
                <span className="text-gray-500">Select promotion...</span>
              </>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] flex w-[min(92vw,960px)] max-w-[var(--radix-popover-content-available-width)] max-h-[var(--radix-popover-content-available-height)] flex-col overflow-hidden p-0"
        align="start"
        collisionPadding={12}
        onWheel={stop_wheel_bubbling}
      >
        <Command shouldFilter={false} className="min-h-0 flex-1">
          <div className="flex shrink-0 flex-wrap items-end gap-2 border-b px-2 py-2 bg-gray-50">
            <div className="flex-1 min-w-[140px] space-y-0.5">
              <Label className="flex items-center text-[10px] text-gray-500 uppercase tracking-wide">
                Customer
                {filterCustomer.trim() && <ActiveFilterIcon />}
              </Label>
              <Input
                className="h-8 text-xs"
                value={filterCustomer}
                onInput={(e: Event) => setFilterCustomer((e.target as HTMLInputElement).value)}
                placeholder="Matches promo customer or retailer"
              />
            </div>
            <div className="flex-1 min-w-[100px] space-y-0.5">
              <Label className="flex items-center text-[10px] text-gray-500 uppercase tracking-wide">
                Promo type
                {filterPromoType.trim() && <ActiveFilterIcon />}
              </Label>
              <Input
                className="h-8 text-xs"
                value={filterPromoType}
                onInput={(e: Event) => setFilterPromoType((e.target as HTMLInputElement).value)}
                placeholder="e.g. TPR"
              />
            </div>
            <div className="w-[150px] space-y-0.5">
              <Label className="flex items-center text-[10px] text-gray-500 uppercase tracking-wide">
                As of date
                {filterAsOfDate.trim() && <ActiveFilterIcon />}
              </Label>
              <Input
                className="h-8 text-xs"
                type="date"
                value={filterAsOfDate}
                onInput={(e: Event) => setFilterAsOfDate((e.target as HTMLInputElement).value)}
              />
            </div>
            <Button type="button" variant="ghost" className="h-8 text-xs shrink-0" onClick={clearStructuredFilters}>
              Clear filters
            </Button>
            {hasActiveStructuredFilters && (
              <p className="basis-full text-[11px] text-gray-500">
                Showing {structuredFilteredMatches.length} of {matches.length} promotions — filters were pre-filled
                from this deduction.{" "}
                <button type="button" className="text-blue-600 hover:underline" onClick={clearStructuredFilters}>
                  Show all
                </button>
              </p>
            )}
          </div>
          <CommandInput
            placeholder="Search promotions..."
            value={searchValue}
            onValueChange={setSearchValue}
            onInput={handleSearchChange}
            className="h-9"
          />
          {/* Fixed list height (shrinkable on short viewports) so the popover doesn't resize and
              reposition on every keystroke as the result count changes */}
          <CommandList
            ref={commandListRef}
            className="h-[min(480px,60vh)] min-h-0 max-h-none overflow-y-auto overscroll-y-contain"
            onWheel={stop_wheel_bubbling}
          >
            {loading ? (
              <CommandEmpty>Loading promotions...</CommandEmpty>
            ) : searchValue && searchValue !== debouncedSearchValue ? (
              <CommandEmpty>Searching...</CommandEmpty>
            ) : finalFilteredMatches.length === 0 ? (
              <CommandEmpty>No promotions found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {finalFilteredMatches.map((match, index) => {
                  const display = formatPromotionDisplay(match)
                  const indicators = getMatchIndicators(match)
                  const isSelected = selectedPromotionId === match.promotion.id
                  const isExpanded = expandedPromotionIds.includes(match.promotion.id)
                  
                  // Create searchable text that includes formatted dates
                  let searchableText = `${match.promotion.name} ${match.promotion.description || ''} ${match.promotion.customer || ''} ${match.promotion.retailer || ''} ${match.promotion.products.join(' ')}`
                  
                  if (match.promotion.start_date && match.promotion.end_date) {
                    const startFormatted = formatDate(match.promotion.start_date)
                    const endFormatted = formatDate(match.promotion.end_date)
                    searchableText += ` ${startFormatted.display} ${endFormatted.display} ${startFormatted.numeric} ${endFormatted.numeric} ${startFormatted.full} ${endFormatted.full}`
                  }
                  
                  return (
                    <CommandItem
                      key={match.promotion.id}
                      value={searchableText}
                      className={`${index % 2 === 0 ? "bg-blue-50" : ""} ${
                        isSelected ? "bg-blue-100" : ""
                      }`}
                      onSelect={() => handleSelect(match)}>
                      <div className="flex items-start gap-3 w-full">
                        <Check
                          className={classes(
                            "mr-2 h-4 w-4 mt-0.5 shrink-0",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex items-center gap-2 truncate min-w-0 flex-1">
                              <span className="font-medium truncate">{display.title}</span>
                              {match.promotion.remote_promo_id && (
                                <ClickToCopyRemotePromoId
                                  copyValue={match.promotion.remote_promo_id}
                                  stopPropagation
                                  className="shrink-0 text-xs text-gray-600 hover:underline"
                                >
                                  ({match.promotion.remote_promo_id})
                                </ClickToCopyRemotePromoId>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Star className="h-3 w-3 text-yellow-500" />
                              <span className="text-xs font-medium text-yellow-600">
                                {display.score}%
                              </span>
                            </div>
                          </div>
                          <div className="text-xs text-gray-600 mb-1 truncate">
                            {display.subtitle}
                          </div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-gray-500">
                              {indicators && `Matches: ${indicators}`}
                            </span>
                            <span className="text-xs text-gray-400">
                              {match.match_summary}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                              onMouseDown={(event: MouseEvent) => {
                                event.preventDefault()
                                event.stopPropagation()
                              }}
                              onClick={(event: MouseEvent) => {
                                event.preventDefault()
                                event.stopPropagation()
                                toggleExpandedPromotion(match.promotion.id)
                              }}
                            >
                              {isExpanded ? "View less" : "View more"}
                              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </button>
                          </div>
                          {isExpanded && (
                            <div className="mt-2 rounded border border-blue-200 bg-white p-2 text-xs text-gray-700 space-y-1">
                              {match.promotion.description && (
                                <div>
                                  <span className="font-medium">Description:</span> {match.promotion.description}
                                </div>
                              )}
                              {match.promotion.remote_promo_id && (
                                <div>
                                  <span className="font-medium">Remote ID:</span>{" "}
                                  <ClickToCopyRemotePromoId
                                    copyValue={match.promotion.remote_promo_id}
                                    stopPropagation
                                    className="text-inherit hover:underline"
                                  >
                                    {match.promotion.remote_promo_id}
                                  </ClickToCopyRemotePromoId>
                                </div>
                              )}
                              {match.promotion.customer && (
                                <div>
                                  <span className="font-medium">Customer:</span> {match.promotion.customer}
                                </div>
                              )}
                              {match.promotion.retailer && (
                                <div>
                                  <span className="font-medium">Retailer:</span> {match.promotion.retailer}
                                </div>
                              )}
                              {match.promotion.start_date && match.promotion.end_date && (
                                <div>
                                  <span className="font-medium">Date range:</span>{" "}
                                  {formatDate(match.promotion.start_date).full} - {formatDate(match.promotion.end_date).full}
                                </div>
                              )}
                              {(match.promotion.discount_percentage || match.promotion.discount_amount) && (
                                <div>
                                  <span className="font-medium">Discount:</span>{" "}
                                  {match.promotion.discount_percentage ? `${match.promotion.discount_percentage}%` : ""}
                                  {match.promotion.discount_percentage && match.promotion.discount_amount ? " • " : ""}
                                  {match.promotion.discount_amount ? `$${match.promotion.discount_amount}` : ""}
                                </div>
                              )}
                              {match.promotion.products.length > 0 && (
                                <div>
                                  <span className="font-medium">Products:</span> {match.promotion.products.join(", ")}
                                </div>
                              )}
                              <div>
                                <span className="font-medium">Match summary:</span> {match.match_summary}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

if (import.meta.vitest) {
  const formatDateForTest = (dateString: string) => {
    const date = parseISO(dateString)
    return {
      display: format(date, "MMM d"),
      numeric: format(date, "MM/dd/yy"),
      full: format(date, "MMM d, yyyy"),
    }
  }

  function buildTestMatch(overrides: Partial<PromotionMatch["promotion"]>): PromotionMatch {
    return {
      promotion: {
        id: "promo-1",
        name: "September Promo",
        description: "Base description",
        customer: "Whole Foods",
        retailer: "INFRA",
        remote_promo_id: "RP-1",
        products: ["Granola"],
        promotion_type: "TPR",
        ...overrides,
      },
      match_score: 0.9,
      match_summary: "test",
      customer_match: true,
      retailer_match: true,
      product_match: true,
      date_match: true,
      rate_match: false,
    }
  }

  describe("PromotionSelector search helpers", () => {
    it("matches exact phrase when contiguous text exists", () => {
      const match = buildTestMatch({ name: "INFRA September Launch" })
      expect(matchesExactPhrase(match, "infra september", formatDateForTest)).toBe(true)
    })

    it("supports fallback token matching in reversed order", () => {
      const match = buildTestMatch({ name: "September Launch", retailer: "INFRA" })
      expect(matchesExactPhrase(match, "infra september", formatDateForTest)).toBe(false)
      expect(matchesTokenFallback(match, getSearchTokens("infra september"), formatDateForTest)).toBe(true)
    })

    it("supports fallback token matching across fields", () => {
      const match = buildTestMatch({ name: "Autumn Launch", description: "September event", retailer: "INFRA" })
      expect(matchesTokenFallback(match, getSearchTokens("infra september"), formatDateForTest)).toBe(true)
    })

    it("returns false when at least one token is missing", () => {
      const match = buildTestMatch({ name: "September Launch", retailer: "INFRA" })
      expect(matchesTokenFallback(match, getSearchTokens("infra october"), formatDateForTest)).toBe(false)
    })
  })

  describe("promotionAsOfDateInRange", () => {
    it("returns true when day is inside inclusive range", () => {
      expect(promotionAsOfDateInRange("2024-06-01", "2024-06-30", "2024-06-15")).toBe(true)
    })
    it("returns true on boundary days", () => {
      expect(promotionAsOfDateInRange("2024-06-01", "2024-06-30", "2024-06-01")).toBe(true)
      expect(promotionAsOfDateInRange("2024-06-01", "2024-06-30", "2024-06-30")).toBe(true)
    })
    it("returns false outside range", () => {
      expect(promotionAsOfDateInRange("2024-06-01", "2024-06-30", "2024-05-31")).toBe(false)
      expect(promotionAsOfDateInRange("2024-06-01", "2024-06-30", "2024-07-01")).toBe(false)
    })
    it("treats missing start or end as open", () => {
      expect(promotionAsOfDateInRange(undefined, "2024-06-30", "2024-06-15")).toBe(true)
      expect(promotionAsOfDateInRange("2024-06-01", undefined, "2024-06-15")).toBe(true)
    })
    it("returns false for invalid as_of format", () => {
      expect(promotionAsOfDateInRange("2024-06-01", "2024-06-30", "not-a-date")).toBe(false)
    })
  })

  describe("resolve_seed_as_of_date", () => {
    it("prefers execution date over invoice date", () => {
      expect(
        resolve_seed_as_of_date({ execution_date: "2024-03-10", invoice_date: "2024-04-01" })
      ).toBe("2024-03-10")
    })
    it("falls back to invoice date when execution date is missing, Unknown, or invalid", () => {
      expect(resolve_seed_as_of_date({ invoice_date: "2024-04-01" })).toBe("2024-04-01")
      expect(
        resolve_seed_as_of_date({ execution_date: "Unknown", invoice_date: "2024-04-01" })
      ).toBe("2024-04-01")
      expect(
        resolve_seed_as_of_date({ execution_date: "not-a-date", invoice_date: "2024-04-01" })
      ).toBe("2024-04-01")
    })
    it("handles full ISO timestamps", () => {
      expect(resolve_seed_as_of_date({ execution_date: "2024-03-10T08:30:00Z" })).toBe("2024-03-10")
    })
    it("returns empty when no candidate is valid", () => {
      expect(resolve_seed_as_of_date({})).toBe("")
      expect(resolve_seed_as_of_date({ execution_date: "Unknown", invoice_date: "" })).toBe("")
    })
  })

  describe("buildPromotionSearchSeedFromSelection", () => {
    it("returns undefined for an empty selection", () => {
      expect(buildPromotionSearchSeedFromSelection([])).toBeUndefined()
    })

    it("seeds every field the selected rows agree on", () => {
      const rows = [
        { customer_name: "Kroger", retailer_name: "Kroger", source: "kroger", invoice_date: "2026-07-09" },
        { customer_name: "Kroger", retailer_name: "Kroger", source: "kroger", invoice_date: "2026-07-09" },
      ]
      expect(buildPromotionSearchSeedFromSelection(rows)).toEqual({
        customer_name: "Kroger",
        retailer_name: "Kroger",
        source: "kroger",
        invoice_date: "2026-07-09",
        execution_date: undefined,
      })
    })

    it("drops fields the rows disagree on so a mixed selection is not over-filtered", () => {
      const rows = [
        { customer_name: "Kroger", invoice_date: "2026-07-09" },
        { customer_name: "Kroger", invoice_date: "2026-07-26" },
      ]
      const seed = buildPromotionSearchSeedFromSelection(rows)
      expect(seed?.customer_name).toBe("Kroger")
      expect(seed?.invoice_date).toBeUndefined()
    })

    it("treats missing, empty, and null values as no value", () => {
      const rows = [
        { customer_name: "Kroger", retailer_name: null },
        { customer_name: "Kroger", retailer_name: "  " },
      ]
      const seed = buildPromotionSearchSeedFromSelection(rows)
      expect(seed?.retailer_name).toBeUndefined()
      expect(seed?.customer_name).toBe("Kroger")
    })

    it("keeps a single row's context intact", () => {
      const seed = buildPromotionSearchSeedFromSelection([
        { customer_name: "UNFI", execution_date: "2026-06-01" },
      ])
      expect(seed?.customer_name).toBe("UNFI")
      expect(seed?.execution_date).toBe("2026-06-01")
    })
  })

  describe("relax_seeded_filters", () => {
    const seed = { customerNeedle: "unfi", asOfDateStr: "2024-06-15" }
    const unfiInJune = buildTestMatch({
      id: "p-unfi-june",
      customer: "UNFI West",
      start_date: "2024-06-01",
      end_date: "2024-06-30",
    })
    const unfiInJanuary = buildTestMatch({
      id: "p-unfi-jan",
      customer: "UNFI West",
      start_date: "2024-01-01",
      end_date: "2024-01-31",
    })
    const keheInJune = buildTestMatch({
      id: "p-kehe-june",
      customer: "KeHE",
      start_date: "2024-06-01",
      end_date: "2024-06-30",
    })

    it("keeps both filters when they already find a match", () => {
      expect(relax_seeded_filters([unfiInJune], seed)).toEqual(seed)
    })
    it("drops the date first when only the customer matches", () => {
      expect(relax_seeded_filters([unfiInJanuary], seed)).toEqual({
        customerNeedle: "unfi",
        asOfDateStr: "",
      })
    })
    it("drops the customer when only the date matches", () => {
      expect(relax_seeded_filters([keheInJune], seed)).toEqual({
        customerNeedle: "",
        asOfDateStr: "2024-06-15",
      })
    })
    it("clears both filters when nothing matches either", () => {
      const offSeason = buildTestMatch({
        id: "p-other",
        customer: "KeHE",
        start_date: "2024-01-01",
        end_date: "2024-01-31",
      })
      expect(relax_seeded_filters([offSeason], seed)).toEqual({
        customerNeedle: "",
        asOfDateStr: "",
      })
    })
  })

  describe("matchPassesStructuredFilters", () => {
    it("filters by customer needle on promotion customer", () => {
      const match = buildTestMatch({ customer: "UNFI West", retailer: "Other" })
      expect(
        matchPassesStructuredFilters(match, { customerNeedle: "unfi", promoTypeNeedle: "", asOfDateStr: "" })
      ).toBe(true)
      expect(
        matchPassesStructuredFilters(match, { customerNeedle: "wegmans", promoTypeNeedle: "", asOfDateStr: "" })
      ).toBe(false)
    })
    it("filters by customer needle on promotion retailer", () => {
      const match = buildTestMatch({ customer: "", retailer: "Target Corp" })
      expect(
        matchPassesStructuredFilters(match, { customerNeedle: "target", promoTypeNeedle: "", asOfDateStr: "" })
      ).toBe(true)
    })
    it("filters by promo type substring", () => {
      const match = buildTestMatch({ promotion_type: "Display / TPR" })
      expect(
        matchPassesStructuredFilters(match, { customerNeedle: "", promoTypeNeedle: "tpr", asOfDateStr: "" })
      ).toBe(true)
      expect(
        matchPassesStructuredFilters(match, { customerNeedle: "", promoTypeNeedle: "scan", asOfDateStr: "" })
      ).toBe(false)
    })
    it("filters by as-of date within range", () => {
      const match = buildTestMatch({
        start_date: "2024-01-01",
        end_date: "2024-01-31",
      })
      expect(
        matchPassesStructuredFilters(match, { customerNeedle: "", promoTypeNeedle: "", asOfDateStr: "2024-01-15" })
      ).toBe(true)
      expect(
        matchPassesStructuredFilters(match, { customerNeedle: "", promoTypeNeedle: "", asOfDateStr: "2024-02-01" })
      ).toBe(false)
    })
  })
}
